import React, { useState } from "react";
import { Box, Switch } from "@mui/material";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { User } from "../../../interfaces/User";
import { useBanner } from "../../context/BannerContext";
import { useUserActions } from "../../hooks/useUserActions";
import { useUserSettings } from "../../context/UserSettingsContext";
import { ediphiPalette } from "../../styles/colors";
import { DetailCard } from "../../shared/cards/DetailCard";
import { BodyText } from "../../shared/text/BodyText.styled";
import TextIconButton from "../../shared/buttons/TextIconButton";
import { getTenant } from "../../../utils/tenant";
import { LOGOUT_ERROR, LOGOUT_SUCCESS } from "../../constants";
import { browserStore } from "../../../utils/storageKeys";

interface UserCardProps {
  onLogOut: () => void;
}

const UserCard: React.FC<UserCardProps> = ({ onLogOut }) => {
  const { handleLogout } = useUserActions();
  const { showBanner } = useBanner();
  const { settings, updateSettings } = useUserSettings();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleUseEdiphiColorsChange = async (checked: boolean) => {
    await updateSettings({ useEdiphiColors: checked });
  };

  const getUserProfile = (): User | null => {
    const userProfile = browserStore.get("userProfile");
    if (!userProfile) return null;

    try {
      return JSON.parse(userProfile) as User;
    } catch (error) {
      console.warn("Failed to parse userProfile from localStorage:", error);
      return null;
    }
  };

  const user = getUserProfile();
  const userName = user?.full_name || "";
  const userEmail = user?.email || "";
  const tenant = getTenant() ?? "";

  const handleLogoutClick = async () => {
    setLoggingOut(true);
    try {
      await handleLogout();
      showBanner(LOGOUT_SUCCESS, "success");
      onLogOut();
    } catch (error) {
      showBanner(LOGOUT_ERROR, "error");
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <DetailCard
      title={userName}
      subtitle={
        <>
          <BodyText sx={{ color: ediphiPalette.mainTextColor }}>{userEmail}</BodyText>
          <BodyText sx={{ fontStyle: "italic" }}>{`tenant: ${tenant}`}</BodyText>
        </>
      }
      action={
        <TextIconButton
          text="Log Out"
          icon={LogoutOutlinedIcon}
          disabled={loggingOut}
          onClick={() => void handleLogoutClick()}
        />
      }
      footer={
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 28 }}>
          <BodyText
            sx={{ fontWeight: 600, color: ediphiPalette.mainTextColor, cursor: "pointer" }}
            onClick={() => void handleUseEdiphiColorsChange(!settings.useEdiphiColors)}
          >
            Use Ediphi Colors
          </BodyText>
          <Switch
            checked={settings.useEdiphiColors}
            onChange={(event) => void handleUseEdiphiColorsChange(event.target.checked)}
            size="small"
          />
        </Box>
      }
    />
  );
};

export default UserCard;
